import { formatLBP, formatUSD } from '@/lib/api';

export const BANK_CARD_FONT = 'IBM Plex Sans Arabic';
export const BANK_CARD_LOGO_URL = '/icons/family-bank-app-icon.png';
export const BANK_CARD_COLORS = Object.freeze({
  deepIndigo: '#171A3A',
  emerald: '#087F6B',
  lightEmerald: '#22A98B',
  ivory: '#F7F2E7',
  champagne: '#D5B477',
});

export const BANK_CARD_TEMPLATES = Object.freeze({
  portrait: Object.freeze({
    key: 'portrait', label: 'طولية', width: 941, height: 1672,
    logo: Object.freeze({ x: 247, y: 145, width: 447, height: 447 }),
    title: Object.freeze({ x: 470, y: 655, fontSize: 78 }),
    fields: Object.freeze({
      name: Object.freeze({ x: 95, y: 805, width: 751, height: 145, fontSize: 59, minFontSize: 25, direction: 'rtl' }),
      usd: Object.freeze({ x: 95, y: 1035, width: 751, height: 190, fontSize: 66, minFontSize: 26, direction: 'ltr' }),
      lbp: Object.freeze({ x: 95, y: 1275, width: 751, height: 190, fontSize: 62, minFontSize: 24, direction: 'ltr' }),
    }),
  }),
  landscape: Object.freeze({
    key: 'landscape', label: 'عرضية', width: 1635, height: 962,
    logo: Object.freeze({ x: 136, y: 177, width: 415, height: 415 }),
    title: Object.freeze({ x: 342, y: 705, fontSize: 75 }),
    fields: Object.freeze({
      name: Object.freeze({ x: 735, y: 200, width: 790, height: 142, fontSize: 62, minFontSize: 25, direction: 'rtl' }),
      usd: Object.freeze({ x: 735, y: 425, width: 377, height: 250, fontSize: 64, minFontSize: 25, direction: 'ltr' }),
      lbp: Object.freeze({ x: 1144, y: 425, width: 381, height: 250, fontSize: 60, minFontSize: 23, direction: 'ltr' }),
    }),
  }),
});

let logoPromise;

export function getBankCardData(me) {
  return Object.freeze({
    name: String(me?.display_name || me?.username || '').trim(),
    usd: formatUSD(me?.balance_usd || 0),
    lbp: formatLBP(me?.balance_lbp || 0),
  });
}

export function fieldStyle(template, field) {
  return {
    left: `${(field.x / template.width) * 100}%`,
    top: `${(field.y / template.height) * 100}%`,
    width: `${(field.width / template.width) * 100}%`,
    height: `${(field.height / template.height) * 100}%`,
  };
}

function loadLogo() {
  if (!logoPromise) {
    logoPromise = new Promise((resolve, reject) => {
      const image = new Image();
      image.decoding = 'async';
      image.onload = () => resolve(image);
      image.onerror = () => {
        logoPromise = undefined;
        reject(new Error(`تعذر تحميل شعار بنك العائلة: ${BANK_CARD_LOGO_URL}`));
      };
      image.src = BANK_CARD_LOGO_URL;
    });
  }
  return logoPromise;
}

async function waitForCardFont(data) {
  if (!document.fonts) return;
  const samples = [data.name, data.usd, data.lbp];
  await Promise.allSettled(samples.map(sample => document.fonts.load(`800 60px "${BANK_CARD_FONT}"`, sample)));
  await document.fonts.ready;
}

function fitCanvasText(context, value, field) {
  const text = String(value ?? '');
  const availableWidth = field.width * 0.92;
  let fontSize = field.fontSize;
  while (fontSize > field.minFontSize) {
    context.font = `800 ${fontSize}px "${BANK_CARD_FONT}", Arial, sans-serif`;
    if (context.measureText(text).width <= availableWidth) break;
    fontSize -= 1;
  }
  context.font = `800 ${fontSize}px "${BANK_CARD_FONT}", Arial, sans-serif`;
  if (context.measureText(text).width <= availableWidth) return text;
  const characters = Array.from(text);
  while (characters.length > 1 && context.measureText(`${characters.join('')}…`).width > availableWidth) characters.pop();
  return `${characters.join('')}…`;
}

function drawField(context, value, field) {
  context.save();
  context.fillStyle = BANK_CARD_COLORS.ivory;
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.direction = field.direction;
  const text = fitCanvasText(context, value, field);
  context.fillText(text, field.x + (field.width / 2), field.y + (field.height * 0.66));
  context.restore();
}

function roundedRect(context, x, y, width, height, radius) {
  const r = Math.min(radius, width / 2, height / 2);
  context.beginPath();
  context.moveTo(x + r, y);
  context.arcTo(x + width, y, x + width, y + height, r);
  context.arcTo(x + width, y + height, x, y + height, r);
  context.arcTo(x, y + height, x, y, r);
  context.arcTo(x, y, x + width, y, r);
  context.closePath();
}

function drawCardBase(context, template, logo) {
  const { deepIndigo, emerald, lightEmerald, ivory, champagne } = BANK_CARD_COLORS;
  const gradient = context.createLinearGradient(0, 0, template.width, template.height);
  gradient.addColorStop(0, deepIndigo);
  gradient.addColorStop(0.58, deepIndigo);
  gradient.addColorStop(1, emerald);
  context.fillStyle = gradient;
  context.fillRect(0, 0, template.width, template.height);
  context.save();
  context.globalAlpha = 0.74;
  context.fillStyle = emerald;
  context.beginPath();
  context.moveTo(0, 0);
  context.lineTo(template.width * 0.43, 0);
  context.bezierCurveTo(template.width * 0.2, template.height * 0.09, template.width * 0.23, template.height * 0.22, 0, template.height * 0.31);
  context.closePath();
  context.fill();
  context.fillStyle = lightEmerald;
  context.beginPath();
  context.moveTo(0, template.height * 0.81);
  context.bezierCurveTo(template.width * 0.31, template.height * 0.87, template.width * 0.58, template.height * 0.97, template.width, template.height * 0.86);
  context.lineTo(template.width, template.height);
  context.lineTo(0, template.height);
  context.closePath();
  context.fill();
  context.restore();
  context.strokeStyle = champagne;
  context.lineWidth = 4;
  context.beginPath();
  context.moveTo(0, template.height * 0.17);
  context.bezierCurveTo(template.width * 0.16, template.height * 0.1, template.width * 0.25, template.height * 0.04, template.width * 0.37, 0);
  context.stroke();
  context.strokeStyle = champagne;
  context.lineWidth = Math.max(8, template.width * 0.008);
  roundedRect(context, 18, 18, template.width - 36, template.height - 36, 42);
  context.stroke();

  context.drawImage(logo, template.logo.x, template.logo.y, template.logo.width, template.logo.height);
  context.fillStyle = champagne;
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.direction = 'rtl';
  context.font = `800 ${template.title.fontSize}px "${BANK_CARD_FONT}", Arial, sans-serif`;
  context.fillText('بنك العائلة', template.title.x, template.title.y);
  if (template.key === 'landscape') {
    context.globalAlpha = 0.7;
    context.fillRect(660, 165, 3, 560);
    context.globalAlpha = 1;
  }
  const labels = { name: 'اسم صاحب الحساب', usd: 'الرصيد بالدولار', lbp: 'الرصيد بالليرة' };
  Object.entries(template.fields).forEach(([key, field]) => {
    const panel = context.createLinearGradient(field.x, field.y, field.x + field.width, field.y + field.height);
    panel.addColorStop(0, key === 'lbp' ? deepIndigo : emerald);
    panel.addColorStop(1, deepIndigo);
    context.fillStyle = panel;
    context.strokeStyle = key === 'lbp' ? champagne : lightEmerald;
    context.lineWidth = 3;
    roundedRect(context, field.x, field.y, field.width, field.height, 32);
    context.fill();
    context.stroke();
    context.fillStyle = key === 'name' ? champagne : ivory;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.direction = 'rtl';
    context.font = `700 ${Math.max(27, Math.round(field.fontSize * 0.5))}px "${BANK_CARD_FONT}", Arial, sans-serif`;
    context.fillText(labels[key], field.x + (field.width / 2), field.y + field.height * 0.28);
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise(resolve => canvas.toBlob(resolve, type, quality));
}

export async function renderBankCard(format, data) {
  const template = BANK_CARD_TEMPLATES[format];
  if (!template) throw new Error('صيغة بطاقة غير معروفة');
  if (!data?.name) throw new Error('اسم صاحب الحساب غير متاح حالياً');
  const [logo] = await Promise.all([loadLogo(), waitForCardFont(data)]);
  const canvas = document.createElement('canvas');
  canvas.width = template.width;
  canvas.height = template.height;
  const context = canvas.getContext('2d', { alpha: false });
  if (!context) throw new Error('المتصفح لا يدعم إنشاء صورة البطاقة');
  drawCardBase(context, template, logo);
  Object.entries(template.fields).forEach(([key, field]) => drawField(context, data[key], field));
  let blob = await canvasToBlob(canvas, 'image/webp', 0.9);
  let extension = 'webp';
  let mimeType = 'image/webp';
  if (!blob || blob.type !== 'image/webp') {
    blob = await canvasToBlob(canvas, 'image/jpeg', 0.9);
    extension = 'jpg';
    mimeType = 'image/jpeg';
  }
  canvas.width = 0;
  canvas.height = 0;
  if (!blob) throw new Error('تعذر إنشاء ملف البطاقة');
  return { blob, extension, mimeType, width: template.width, height: template.height };
}

export function downloadBankCardFile(rendered, format) {
  const url = URL.createObjectURL(rendered.blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `family-bank-card-${format}.${rendered.extension}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
